import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { assetsApi } from '../../lib/assetsApi';
import { useNotification } from '../../contexts/NotificationContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiImage, FiFile, FiLoader, FiRefreshCw } = FiIcons;

export default function AssetPreview() {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const { showError } = useNotification();

  const loadAssets = async () => {
    setLoading(true);
    
    try {
      const result = await assetsApi.listAssets();
      setAssets(result.assets || []);
    } catch (error) {
      showError('Failed to load assets: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAssets();
  }, []);

  const getAssetPreview = (asset) => {
    if (asset.kind === 'logo' || asset.kind === 'watermark' || asset.kind === 'favicon') {
      return (
        <div className="w-full h-32 bg-gray-100 rounded-lg flex items-center justify-center">
          <SafeIcon icon={FiImage} className="w-8 h-8 text-gray-400" />
        </div>
      );
    }
    
    return (
      <div className="w-full h-32 bg-gray-100 rounded-lg flex items-center justify-center">
        <SafeIcon icon={FiFile} className="w-8 h-8 text-gray-400" />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <SafeIcon icon={FiLoader} className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Asset Preview</h1>
            <p className="text-gray-600">Preview all uploaded assets and files</p>
          </div>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={loadAssets}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <SafeIcon icon={FiRefreshCw} className="w-4 h-4 mr-2" />
            Refresh
          </motion.button>
        </div>
      </div>

      {assets.length === 0 ? (
        <div className="text-center py-12">
          <SafeIcon icon={FiImage} className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Assets Found</h3>
          <p className="text-gray-600">Upload some files to see them here</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {assets.map((asset, index) => (
            <motion.div
              key={asset.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-lg shadow-md overflow-hidden"
            >
              {getAssetPreview(asset)}
              
              <div className="p-4">
                <h3 className="font-medium text-gray-900 truncate mb-1">
                  {asset.name}
                </h3>
                
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <span className="capitalize">{asset.kind}</span>
                  {asset.size && (
                    <span>{(asset.size / 1024 / 1024).toFixed(2)} MB</span>
                  )}
                </div>

                {asset.created_at && (
                  <p className="text-xs text-gray-400 mt-2">
                    {new Date(asset.created_at).toLocaleDateString()}
                  </p>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}